'use client'

import {useTranslations} from 'next-intl'
import {useEffect, useState} from 'react'
import {Button, Icon} from '@/design-system'
import {api, ApiError} from '@/lib/api/client'
import type {SavedSearch, SavedSearchFilters} from '@/lib/api/types'
import type {SearchMode} from '@/lib/search/filters'
import {useToast} from '../Toast'
import './saved.css'

/** The API's cap on saved searches; kept in step with `MAX_SAVED_SEARCHES`. */
const MAX_SAVED_SEARCHES = 10

/**
 * Save the search on screen, or stop saving it.
 *
 * Optimistic, like `SaveButton`: the button changes the moment it is pressed
 * and goes back with a toast if the request fails. Whether this search is
 * already saved comes from the page, so nothing is fetched to render it.
 *
 * Opening a saved search is what resets its "new" count on the dashboard, so
 * a saved search tells the API it was seen once the page has loaded — from
 * here rather than from the server render, so a prefetch or a crawler never
 * counts as the student looking.
 */
export function SaveSearchButton({
  mode,
  filters,
  savedId: initialId
}: {
  mode: SearchMode
  /** The search on screen, in the stored form (`toSavedFilters`). */
  filters: SavedSearchFilters
  savedId: string | null
}) {
  const t = useTranslations('savedSearches')
  const tErrors = useTranslations('errors')
  const toast = useToast()
  const [savedId, setSavedId] = useState<string | null>(initialId)
  const [pending, setPending] = useState(false)

  // A new search arrives as new props on the same island (filters change
  // without a remount), so the saved state follows the page.
  useEffect(() => setSavedId(initialId), [initialId])

  useEffect(() => {
    if (!initialId) return
    // Best effort: a missed "seen" only leaves a badge up until next time.
    api.post(`/me/searches/${initialId}/seen`).catch(() => {})
  }, [initialId])

  async function toggle() {
    const previous = savedId
    setPending(true)
    try {
      if (previous) {
        setSavedId(null)
        await api.delete(`/me/searches/${previous}`)
      } else {
        setSavedId('pending')
        const saved = await api.put<SavedSearch>('/me/searches', {mode, filters})
        setSavedId(saved.id)
        toast.show(t('savedToast'))
      }
    } catch (error) {
      setSavedId(previous)
      const limitReached = error instanceof ApiError && error.code === 'conflict'
      toast.show(
        limitReached
          ? t('limitReached', {max: MAX_SAVED_SEARCHES})
          : tErrors(error instanceof ApiError ? error.messageKey : 'internal'),
        'error'
      )
    } finally {
      setPending(false)
    }
  }

  const isSaved = savedId !== null

  return (
    <Button
      type="button"
      variant={isSaved ? 'secondary' : 'ghost'}
      size="sm"
      aria-pressed={isSaved}
      disabled={pending}
      onClick={toggle}
      data-testid="save-search"
    >
      <Icon name={isSaved ? 'bookmark-check' : 'bookmark'} size={16} />
      <span>{isSaved ? t('saved') : t('save')}</span>
    </Button>
  )
}
